/**
 * 项目支付明细附件：凭证列用缩略图快速核对，附件列可以直接补传付款资料。
 * 两列分别导出，供平铺明细与按收款方展开的明细复用；不改金额、审批状态或账目锁定规则。
 * 原件通过已有分片上传器保存，关联由项目支出专属接口校验，历史凭证保持只读。
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, FileText, Image as ImageIcon, Loader2, Upload } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { authToken } from "../../lib/api";
import { downloadFile } from "../../lib/fileDownload";
import { isCanceled, uploadChunked, uploadErrText } from "../../lib/upload";
import { isScannable, uploadScannedDocument } from "../../lib/documentScanner";
import FinanceDocumentScanner from "./FinanceDocumentScanner";
import DocumentScanVersions from "./DocumentScanVersions";
import { ProjectPdfPreview } from "../pdf/ProjectPdfPreview";

// 与后端项目支出附件白名单、单文件限制保持一致；财务资料不接受脚本或可执行文件。
const ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.bmp,.heic,.heif,.tif,.tiff,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt";
const EXTENSIONS = new Set(ACCEPT.split(","));
const MAX_BYTES = 25 * 1024 * 1024;
const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp", "image/avif"]);
const mimeOf = file => String(file.contentType || "").toLowerCase().split(";")[0];
const isImage = file => IMAGE_TYPES.has(mimeOf(file));
const isPdf = file => mimeOf(file) === "application/pdf";
const rowKey = row => `${row.kind}-${row.id}`;

/**
 * 文件地址仅供列表缩略图使用：图片 download 默认读取压缩预览，PDF thumb 读取首页。
 * 不给图片加 orig/src，也不走会直接回原图的 thumb 接口，避免 44px 方块读取整张大凭证。
 */
const fileUrl = (api, file, thumb = false) => {
  const base = api?.defaults?.baseURL || `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
  return `${base.replace(/\/$/, "")}/files/${encodeURIComponent(file.fileId)}/${thumb ? "thumb" : "download"}?auth=${encodeURIComponent(authToken())}`;
};

/** 固定缩略图尺寸防止表格跳行；缺图时退回文件图标，仍可点击查看或下载。 */
function AttachmentThumbnail({ api, file }) {
  const [failed, setFailed] = useState(false);
  const canShow = isImage(file) || file.hasThumb;
  return canShow && !failed
    ? <img src={fileUrl(api, file, !isImage(file) && !!file.hasThumb)} alt="" loading="lazy" width="44" height="44" className="h-full w-full object-cover" onError={() => setFailed(true)} />
    : <span className="flex h-full flex-col items-center justify-center gap-0.5 text-muted-foreground">
      {isImage(file) ? <ImageIcon className="h-5 w-5" aria-hidden="true" /> : <FileText className="h-5 w-5" aria-hidden="true" />}
      {isPdf(file) && <span className="text-[10px] font-semibold">PDF</span>}
    </span>;
}

/**
 * 阅读器只读取用户点开的原件，不预加载整张台账；关闭/换文件时撤销 Blob URL 并取消旧请求。
 * 图片与 PDF 才允许页内预览，Office 等原件只提供下载，避免把未知附件当网页执行。
 */
function AttachmentContents({ api, file, zh }) {
  const [state, setState] = useState({ loading: true, url: "", type: "", failed: false });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let active = true, objectUrl = "";
    setState({ loading: true, url: "", type: "", failed: false });
    // PDF 通过已鉴权的逐页接口渲染，无需下载整份文件或依赖浏览器插件。
    if (isPdf(file) || !isImage(file)) {
      setState({ loading: false, url: "", type: "", failed: false });
      return () => { active = false; controller.abort(); };
    }
    // src 优先读取历史文件挂接的真正源文件，orig 保证未挂源文件时也不回压缩版。
    api.get(`/files/${encodeURIComponent(file.fileId)}/download`, { params: { src: 1, orig: 1 }, responseType: "blob", signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        // 以实际返回 MIME 为准，文件名或数据库标签不能把错误页升级成可执行预览。
        const type = String(data?.type || "").toLowerCase().split(";")[0];
        if (!IMAGE_TYPES.has(type) && type !== "application/pdf") throw new Error("Unsupported preview type");
        objectUrl = URL.createObjectURL(data);
        setState({ loading: false, url: objectUrl, type, failed: false });
      })
      .catch(() => { if (active) setState({ loading: false, url: "", type: "", failed: true }); });
    return () => { active = false; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [api, file, retry]);
  const fallback = state.failed || (!isImage(file) && !isPdf(file));
  if (isPdf(file) || (!state.failed && state.type === "application/pdf")) return <div className="min-h-0 flex-1 overflow-hidden rounded-xl border bg-muted/40 p-1 sm:p-2" data-testid="outflow-attachment-viewport">
    <div className="h-full min-h-0" data-testid="outflow-attachment-pdf"><ProjectPdfPreview api={api} file={file} zh={zh} allowBrowserFallback={false} showThumbnails={false} /></div>
  </div>;
  return <div className="min-h-0 flex-1 overflow-auto rounded-xl border bg-muted/40 p-1 sm:p-2" data-testid="outflow-attachment-viewport">
    {state.loading && <p role="status" className="flex h-full min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin motion-reduce:animate-none" aria-hidden="true" />{zh ? "正在加载凭证…" : "Loading receipt…"}</p>}
    {!fallback && state.url && <img src={state.url} alt={file.filename || (zh ? "支付凭证" : "Payment receipt")} className="h-full w-full min-h-0 min-w-0 object-contain" data-testid="outflow-attachment-image" onError={() => setState(s => ({ ...s, failed: true }))} />}
    {fallback && <div className="flex h-full min-h-48 flex-col items-center justify-center gap-3 p-4 text-center text-muted-foreground">
      <FileText className="h-9 w-9" aria-hidden="true" />
      <p role={state.failed ? "alert" : undefined} className="text-sm">{state.failed ? (zh ? "凭证预览失败，可重试或下载原件。" : "Preview failed. Retry or download the original.") : (zh ? "此格式请下载原件查看。" : "Download the original to view this format.")}</p>
      {state.failed && <Button type="button" variant="outline" onClick={() => setRetry(n => n + 1)}>{zh ? "重试" : "Retry"}</Button>}
    </div>}
  </div>;
}

/**
 * 凭证列：api 为当前鉴权客户端，row 为支出行，zh 控制双语。
 * 最多两张缩略图和可点击的余量入口保证行高稳定；弹窗支持逐张查看所有附件。
 */
export function OutflowAttachmentPreview({ api, row, zh = true }) {
  const files = Array.isArray(row.attachments) ? row.attachments.filter(file => file?.fileId) : [];
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const trigger = useRef(null);
  const current = selected === null ? null : files[Math.min(selected, files.length - 1)];
  const open = (event, index) => { event.stopPropagation(); trigger.current = event.currentTarget; setSelected(index); };
  /** 跨域 a[download] 会被浏览器忽略并离开财务页，统一用已有 Blob 下载入口保留页面上下文。 */
  const download = async () => {
    if (!current || downloading) return;
    setDownloading(true);
    try {
      // 不转 JPEG：收款凭证需要保留原始文件字节；共享入口以 src=1 获取已挂接的源文件。
      await downloadFile(current.fileId, current.filename, { jpeg: false, token: authToken() });
    } catch (_) { toast.error(zh ? "下载失败，请重试。" : "Download failed. Please retry."); }
    finally { setDownloading(false); }
  };
  if (!files.length) return <span className="text-xs text-muted-foreground" data-testid={`outflow-no-attachments-${rowKey(row)}`}>{zh ? "暂无凭证" : "No receipt"}</span>;
  return <>
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`outflow-attachments-${rowKey(row)}`}>
      {files.slice(0, 2).map((file, index) => <button type="button" key={file.id || file.fileId} onClick={event => open(event, index)}
        className="h-11 w-11 shrink-0 cursor-zoom-in overflow-hidden rounded-lg border bg-muted/40 transition-colors hover:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 active:border-brand-500"
        aria-label={`${zh ? "预览凭证" : "Preview receipt"}：${file.filename || (index + 1)}`} title={file.filename || (zh ? "支付凭证" : "Payment receipt")} data-testid={`outflow-attachment-${rowKey(row)}-${index}`}>
        <AttachmentThumbnail api={api} file={file} />
      </button>)}
      {files.length > 2 && <Button type="button" variant="ghost" size="sm" className="h-11 min-w-11 px-1.5 tabular-nums" onClick={event => open(event, 2)} aria-label={zh ? `查看全部 ${files.length} 个附件` : `View all ${files.length} attachments`}>+{files.length - 2}</Button>}
    </div>
    <Dialog open={!!current} onOpenChange={isOpen => !isOpen && setSelected(null)}>
      {/* 财务凭证打开即使用接近整屏的阅读区域，保留安全区、关闭按钮和键盘焦点返回。 */}
      {current && <DialogContent className="!flex h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] !max-h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] !w-[calc(100vw-1rem)] !max-w-none flex-col gap-2 !overflow-hidden p-2 sm:p-3" closeLabel={zh ? "关闭凭证" : "Close receipt"} data-testid="outflow-attachment-preview"
        onCloseAutoFocus={event => { event.preventDefault(); trigger.current?.focus(); }}>
        <DialogHeader className="shrink-0 text-left">
          <DialogTitle className="break-words text-base [overflow-wrap:anywhere]">{current.filename || (zh ? "支付凭证" : "Payment receipt")}</DialogTitle>
          <DialogDescription>{row.payee || (zh ? "支付凭证" : "Payment receipt")}</DialogDescription>
        </DialogHeader>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {files.length > 1 && <><Button type="button" size="icon" variant="outline" className="h-11 w-11" disabled={selected <= 0} onClick={() => setSelected(n => n - 1)} aria-label={zh ? "上一份凭证" : "Previous receipt"} title={zh ? "上一份凭证" : "Previous receipt"}><ChevronLeft aria-hidden="true" /></Button>
              <span className="text-sm tabular-nums" aria-live="polite">{Math.min(selected + 1, files.length)} / {files.length}</span>
              <Button type="button" size="icon" variant="outline" className="h-11 w-11" disabled={selected >= files.length - 1} onClick={() => setSelected(n => n + 1)} aria-label={zh ? "下一份凭证" : "Next receipt"} title={zh ? "下一份凭证" : "Next receipt"}><ChevronRight aria-hidden="true" /></Button></>}
          </div>
          <Button type="button" variant="outline" className="min-h-11" onClick={download} disabled={downloading} data-testid="outflow-attachment-download">{downloading ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Download aria-hidden="true" />}{downloading ? (zh ? "下载中…" : "Downloading…") : (zh ? "下载原件" : "Download original")}</Button>
        </div>
        {current.documentScan && <DocumentScanVersions api={api} zh={zh} fileId={current.fileId} />}
        <AttachmentContents key={current.id || current.fileId} api={api} file={current} zh={zh} />
      </DialogContent>}
    </Dialog>
  </>;
}

/**
 * 附件列：canUpload 仅表示财务操作权限，与 row.locked 无关，已审批出款仍可补充资料。
 * 逐个上传/关联，失败保留剩余队列；文件已落盘但关联失败时重试复用 fileId，避免重复上传。
 * onChanged 在成功或部分成功后刷新两种台账视图；任何情况下均不调用金额/审批更新接口。
 */
export function OutflowAttachmentUpload({ api, pid, row, zh = true, canUpload = false, onChanged, onBusyChange }) {
  const input = useRef(null), pending = useRef([]), active = useRef(true), running = useRef(false), controller = useRef(null);
  const [progress, setProgress] = useState(null), [error, setError] = useState("");
  const [scanFiles, setScanFiles] = useState([]);
  // 编辑弹窗在扫描/上传时保留现场，防止关闭弹窗中断文件关联。
  useEffect(() => { onBusyChange?.(Boolean(progress) || scanFiles.length > 0); }, [progress, scanFiles.length, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  const passthrough = useRef([]);
  useEffect(() => { active.current = true; return () => { active.current = false; controller.current?.abort(); }; }, []);
  const run = async () => {
    if (!canUpload || running.current || !pending.current.length) return;
    running.current = true;
    controller.current = new AbortController();
    const signal = controller.current.signal;
    let saved = 0;
    const total = pending.current.length;
    setError("");
    try {
      while (pending.current.length) {
        const item = pending.current[0];
        if (signal.aborted) break;
        setProgress({ name: item.file.name, pct: 0, state: "uploading", number: saved + 1, total });
        if (!item.fileId) {
          // 前端不压原件；后端共享压缩器另存预览，不覆盖原文件，兼顾凭证保真与列表流量。
          const uploader = item.file.documentScan ? uploadScannedDocument : uploadChunked;
          const uploaded = await uploader(api, item.file, { shrink: false, signal,
            onProgress: value => { if (active.current) setProgress({ ...value, name: item.file.name, number: saved + 1, total }); } });
          item.fileId = uploaded.fileId;
        }
        if (signal.aborted) break;
        setProgress({ name: item.file.name, pct: 100, state: "linking", number: saved + 1, total });
        await api.post(`/finance/project/${encodeURIComponent(pid)}/outflows/${encodeURIComponent(row.kind)}/${encodeURIComponent(row.id)}/attachments`, { fileId: item.fileId }, { signal });
        pending.current.shift(); saved += 1;
      }
      if (active.current && !signal.aborted) toast.success(zh ? `已上传 ${saved} 个附件` : `${saved} attachment(s) uploaded`);
    } catch (failure) {
      if (active.current) {
        const message = isCanceled(failure) ? (zh ? "上传已暂停，可重试继续。" : "Upload paused. Retry to continue.") : uploadErrText(failure, zh);
        setError(`${pending.current[0]?.file.name || ""}：${message}${saved ? (zh ? `（已保存 ${saved} 个）` : ` (${saved} saved)`) : ""}`);
      }
    } finally {
      running.current = false;
      if (active.current) {
        setProgress(null);
        if (signal.aborted && pending.current.length) setError(zh ? "上传已暂停，可重试继续。" : "Upload paused. Retry to continue.");
        // 刷新失败不能被当作上传失败；已成功关联的文件已从队列移除，禁止再次提交。
        if (saved) {
          try { await onChanged?.(); }
          catch (_) { toast.error(zh ? "附件已保存，请刷新列表查看。" : "Attachments saved. Refresh the list to view them."); }
        }
      }
    }
  };
  const choose = event => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || running.current || !canUpload) return;
    const invalid = files.find(file => !file.size || file.size > MAX_BYTES || !EXTENSIONS.has(`.${file.name.split(".").pop().toLowerCase()}`));
    if (invalid) { setError(zh ? `${invalid.name}：请选择图片、PDF、Office、CSV 或 TXT，每个文件须为 1 字节至 25 MB。` : `${invalid.name}: choose an image, PDF, Office, CSV or TXT file, 1 byte–25 MB each.`); return; }
    const documents = files.filter(isScannable);
    // 扫描确认之前不上传任何文件；Office 等附件保留原来的上传路径。
    if (documents.length) {
      passthrough.current = files.filter(file => !documents.includes(file));
      setScanFiles(documents);
    } else {
      pending.current = files.map(file => ({ file })); run();
    }
  };
  const prepared = ({ scanFile, originals }) => {
    pending.current = [...(scanFile ? [scanFile] : originals), ...passthrough.current].map(file => ({ file }));
    setScanFiles([]); passthrough.current = []; run();
  };
  if (!canUpload) return <span className="text-xs text-muted-foreground" data-testid={`outflow-attachment-readonly-${rowKey(row)}`}>{zh ? "只读" : "Read only"}</span>;
  const label = progress?.state === "offline" ? (zh ? "等待网络…" : "Waiting for network…")
    : progress?.state === "linking" ? (zh ? "正在保存…" : "Saving…")
      : progress ? `${zh ? "上传中" : "Uploading"} ${progress.pct || 0}%` : (zh ? "上传" : "Upload");
  return <div className="min-w-0 space-y-1" data-testid={`outflow-upload-${rowKey(row)}`}>
    <input ref={input} type="file" accept={ACCEPT} multiple hidden disabled={!!progress} onChange={choose} aria-label={zh ? "选择支付附件" : "Choose payment attachments"} data-testid={`outflow-file-input-${rowKey(row)}`} />
    {/* 列头已说明“附件”，按钮简写避免窄屏中文断成三字加一字；完整名称保留给辅助技术。 */}
    <Button type="button" variant="outline" size="sm" className="h-auto min-h-11 min-w-0 max-w-full gap-1.5 whitespace-normal px-2 py-2" disabled={!!progress} onClick={() => input.current?.click()} aria-label={progress ? label : (zh ? "上传附件" : "Upload attachment")} title={zh ? "上传附件：图片 / PDF / Office / CSV / TXT，单个文件不超过 25 MB" : "Upload attachment: Image / PDF / Office / CSV / TXT, up to 25 MB each"}>
      {progress ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Upload aria-hidden="true" />}<span className="min-w-0 leading-4 [overflow-wrap:anywhere]">{label}</span>
    </Button>
    {progress && <div className="space-y-1"><p role="status" className="break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">{progress.number}/{progress.total} · {progress.name}</p><Button type="button" variant="link" size="sm" className="min-h-9 px-1" onClick={() => controller.current?.abort()}>{zh ? "暂停" : "Pause"}</Button></div>}
    {error && <><p role="alert" className="break-words text-xs text-destructive [overflow-wrap:anywhere]">{error}</p>{pending.current.length > 0 && !progress && <Button type="button" variant="link" size="sm" className="min-h-9 px-1" onClick={run}>{zh ? "重试上传" : "Retry upload"}</Button>}</>}
    <FinanceDocumentScanner api={api} zh={zh} files={scanFiles} maxOriginalFiles={Number.POSITIVE_INFINITY} maxBytes={MAX_BYTES} onClose={() => { setScanFiles([]); passthrough.current = []; }} onPrepared={prepared} />
  </div>;
}
